import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { footerLogo } from "../../constants/assets";
import { useBookDemo } from "../../context/BookDemoContext";

// Link targets match the order of footer.columns.<key>.links in the locale files.
const footerColumns = [
  {
    key: "platform",
    links: ["/solutions", "/technology", "/technology#security", "/privacy"],
  },
  {
    key: "capabilities",
    links: ["/technology", "/developers", "/solutions", "/partners"],
  },
  {
    key: "developers",
    links: ["/developers", "/documentation"],
  },
  {
    key: "industries",
    links: [
      "/industries#airlines",
      "/industries#travel",
      "/industries#fintech",
      "/industries#retail",
      "/industries#enterprises",
    ],
  },
  {
    key: "company",
    links: ["/contact", "/terms", "/privacy"],
  },
];

// Hidden for now: footer social links markup lives below, commented out.
// // Order matches footer.social in the locale files.
// const socialLinks = [
//   { key: "linkedin", href: "#linkedin" },
//   { key: "x", href: "#x" },
//   { key: "youtube", href: "#youtube" },
// ];
//
// function SocialIcon({ type }) {
//   const common = {
//     width: 18,
//     height: 18,
//     viewBox: "0 0 24 24",
//     fill: "currentColor",
//     "aria-hidden": true,
//   };
//
//   if (type === "linkedin") {
//     return (
//       <svg {...common}>
//         <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9.5h4v11H3v-11zm7 0h3.8v1.5h.05c.53-.95 1.83-1.95 3.77-1.95 4.03 0 4.78 2.5 4.78 5.75v5.7h-4v-5.05c0-1.2-.02-2.75-1.72-2.75-1.72 0-1.98 1.3-1.98 2.66v5.14h-4v-11z" />
//       </svg>
//     );
//   }
//
//   if (type === "x") {
//     return (
//       <svg {...common}>
//         <path d="M17.53 3h3.02l-6.6 7.54L21.75 21h-6.08l-4.76-6.22L5.46 21H2.44l7.06-8.07L2.25 3h6.23l4.3 5.69L17.53 3zm-1.06 16.2h1.67L7.6 4.71H5.81l10.66 14.49z" />
//       </svg>
//     );
//   }
//
//   // youtube
//   return (
//     <svg {...common}>
//       <path d="M21.58 7.2a2.5 2.5 0 0 0-1.76-1.77C18.25 5 12 5 12 5s-6.25 0-7.82.43A2.5 2.5 0 0 0 2.42 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .42 4.8 2.5 2.5 0 0 0 1.76 1.77C5.75 19 12 19 12 19s6.25 0 7.82-.43a2.5 2.5 0 0 0 1.76-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.42-4.8zM10 15.02V8.98L15.2 12 10 15.02z" />
//     </svg>
//   );
// }

function FooterArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10.4688 4.375L16.0938 10L10.4688 15.625M15.3125 10H3.90625"
        stroke="currentColor"
        strokeWidth="1.875"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FooterLink({ to, children }) {
  if (to.startsWith("/")) {
    return <Link to={to}>{children}</Link>;
  }

  return <a href={to}>{children}</a>;
}

function Footer() {
  const { t } = useTranslation();
  const { openBookDemo } = useBookDemo();
  const stats = t("footer.stats", { returnObjects: true });

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-layout">
          <div className="footer-main">
            <div className="footer-top">
              <Link className="footer-brand" to="/">
                <img src={footerLogo} alt="R4L Solutions" />
              </Link>
              <div className="footer-stats">
                {stats.map((item) => (
                  <div className="footer-stat" key={item.label}>
                    <strong className="footer-stat-value">{item.value}</strong>
                    <span className="footer-stat-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="footer-columns">
              <div className="footer-about">
                <p className="footer-paragraph">{t("footer.description")}</p>
                {/* Social links (LinkedIn / X / YouTube) are hidden for now. */}
              </div>

              {footerColumns.map((column) => {
                const labels = t(`footer.columns.${column.key}.links`, {
                  returnObjects: true,
                });

                return (
                  <div className="footer-column" key={column.key}>
                    <h3 className="footer-list-heading">
                      {t(`footer.columns.${column.key}.title`)}
                    </h3>
                    <ul className="footer-nav-list">
                      {labels.map((label, index) => (
                        <li key={label}>
                          <FooterLink to={column.links[index]}>
                            {label}
                          </FooterLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="footer-demo-card">
            <h3 className="footer-demo-title">{t("footer.demo.title")}</h3>
            <p className="footer-demo-text">{t("footer.demo.text")}</p>
            <button
              className="btn-cs btn-booking footer-demo-btn"
              type="button"
              onClick={openBookDemo}
            >
              {t("footer.demo.cta")}
              <FooterArrowIcon />
            </button>
          </aside>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">{t("footer.copyright")}</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
